import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/layout/AdminLayout';
import { format } from 'date-fns';
import { Calendar as CalendarIcon, FileText, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Logs = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableDates, setAvailableDates] = useState([]);
  const [logContent, setLogContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingDates, setLoadingDates] = useState(true);

  useEffect(() => {
    fetchAvailableDates();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      fetchLogContent(selectedDate);
    }
  }, [selectedDate]);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchAvailableDates = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/logs/dates`, {
        headers: getAuthHeader(),
      });
      setAvailableDates(response.data);
    } catch (error) {
      console.error('Failed to fetch log dates:', error);
    } finally {
      setLoadingDates(false);
    }
  };

  const fetchLogContent = async (date) => {
    setLoading(true);
    try {
      const dateStr = format(date, 'yyyyMMdd');
      const response = await axios.get(`${BACKEND_URL}/api/logs/${dateStr}`, {
        headers: getAuthHeader(),
      });
      setLogContent(response.data);
    } catch (error) {
      console.error('Failed to fetch log content:', error);
      setLogContent({ date: format(date, 'yyyy-MM-dd'), lines: [], content: 'Failed to load logs.' });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchAvailableDates();
    if (selectedDate) {
      fetchLogContent(selectedDate);
    }
    toast.success('Logs refreshed');
  };

  // Check if a date has logs
  const hasLogs = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return availableDates.some(d => d.date === dateStr);
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="logs-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl font-medium text-[#1A1A1A] tracking-tight">
              Activity Logs
            </h1>
            <p className="text-[#585858] mt-2">
              View system activity logs for auditing
            </p>
          </div>
          <Button
            onClick={handleRefresh}
            variant="outline"
            className="border-[#69111F] text-[#69111F] hover:bg-[#69111F] hover:text-[#F7F0E8]"
            data-testid="refresh-logs-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left - Date Selection */}
          <div className="lg:col-span-1">
            <Card className="bg-white border-[#E6DCD3]">
              <CardHeader className="border-b border-[#E6DCD3]">
                <CardTitle className="font-serif text-lg text-[#1A1A1A]">
                  Select Date
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal border-[#E6DCD3] hover:border-[#69111F]"
                      data-testid="date-picker-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-[#69111F]" />
                      {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-white" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      initialFocus
                      modifiers={{
                        hasLogs: (date) => hasLogs(date),
                      }}
                      modifiersStyles={{
                        hasLogs: { 
                          fontWeight: 'bold',
                          backgroundColor: '#F2EBE3',
                          color: '#69111F'
                        },
                      }}
                    />
                  </PopoverContent>
                </Popover>

                {/* Available Dates List */}
                <div className="mt-4">
                  <p className="text-sm text-[#585858] mb-2">Recent Log Files:</p>
                  {loadingDates ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="w-5 h-5 animate-spin text-[#69111F]" />
                    </div>
                  ) : availableDates.length === 0 ? (
                    <p className="text-sm text-[#8A8A8A]">No logs available yet.</p>
                  ) : (
                    <div className="space-y-1 max-h-60 overflow-y-auto">
                      {availableDates.slice(0, 10).map((log) => (
                        <button
                          key={log.date}
                          onClick={() => setSelectedDate(new Date(log.date))}
                          className={`w-full text-left px-3 py-2 text-sm rounded-sm transition-colors ${
                            format(selectedDate, 'yyyy-MM-dd') === log.date
                              ? 'bg-[#69111F] text-[#F7F0E8]'
                              : 'hover:bg-[#F2EBE3] text-[#585858]'
                          }`}
                        >
                          <FileText className="w-4 h-4 inline mr-2" />
                          {log.date}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right - Log Content */}
          <div className="lg:col-span-3">
            <Card className="bg-white border-[#E6DCD3]">
              <CardHeader className="border-b border-[#E6DCD3]">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-serif text-lg text-[#1A1A1A]">
                    Log Content - {selectedDate ? format(selectedDate, 'PPP') : 'Select a date'}
                  </CardTitle>
                  {logContent && logContent.lines && (
                    <span className="text-sm text-[#585858]">
                      {logContent.lines.length} entries
                    </span>
                  )}
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-[#69111F]" />
                  </div>
                ) : !logContent || logContent.lines.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-[#585858]">
                    <FileText className="w-12 h-12 mb-4 text-[#E6DCD3]" />
                    <p>No logs found for this date.</p>
                    <p className="text-sm text-[#8A8A8A] mt-2">
                      Logs are created when actions are performed in the system.
                    </p>
                  </div>
                ) : (
                  <div className="font-mono text-sm max-h-[600px] overflow-y-auto">
                    <table className="w-full">
                      <thead className="bg-[#F2EBE3] sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left text-[#69111F] font-medium w-12">#</th>
                          <th className="px-4 py-2 text-left text-[#69111F] font-medium w-48">Timestamp</th>
                          <th className="px-4 py-2 text-left text-[#69111F] font-medium">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {logContent.lines.map((line, index) => {
                          // Parse log line: [timestamp] message
                          const match = line.match(/^\[(.+?)\]\s*(.*)$/);
                          const timestamp = match ? match[1] : '';
                          const message = match ? match[2] : line;
                          
                          return (
                            <tr 
                              key={index} 
                              className="border-b border-[#E6DCD3] hover:bg-[#FDFBF8]"
                            >
                              <td className="px-4 py-2 text-[#8A8A8A]">{index + 1}</td>
                              <td className="px-4 py-2 text-[#585858] whitespace-nowrap">{timestamp}</td>
                              <td className="px-4 py-2 text-[#1A1A1A]">{message}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Logs;
